import React from 'react';
import styled from 'styled-components';
import { colors } from "../../utils/colors";
import * as TypographyStyles from '../TypographyStyles';
import { DelayHistory } from './Types/delayHistory';
import { slateColors } from '../ContentPages/AppLandingPage/styles';
import { useTranslation } from 'react-i18next';

const ContainerCard = styled('div')`
    display: flex;
    padding: 1.5rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 1.25rem;
    align-self: start;
    border-radius: 2.5rem;
    border: 1px solid ${slateColors[200]};
    background: white;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
    box-sizing: border-box;
    transition: all 0.3s;
    
    &:hover {
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
        border-color: ${slateColors[300]};
    }
`

const CardHeader = styled('div')`
    display: flex;
    align-items: flex-start;
    gap: 4px;
`

const Chart = styled.div`
display: flex;
flex-direction: column;
align-items: flex-start;
gap: 12px;
align-self: stretch;
`;

const Cohort = styled.div`
display: flex;
align-items: center;
gap: 14px;
align-self: stretch;
`;

const HorizontalBar = styled.div`
display: flex;
height: 16px;
align-items: center;
flex: 1 0 0;
border-radius: 999px;
background: ${slateColors[200]};
`;

interface BarFillProps {
    width: string;
    color: string;  // Add color prop
}

const BarFill = styled.div<BarFillProps>`
    height: 16px;
    border-radius: 999px;
    background-color: ${({ color }) => color};  // Use color prop for background color
    width: ${({ width }) => width};
`;

const CategoryText = styled(TypographyStyles.BodySRegular)`
color: ${colors.grey800};
width: 60px;
`;

const ValueText = styled(TypographyStyles.BodySRegular)`
color: ${colors.grey800};
width: 25px;
text-align: right;
`;


interface DelayDistributionCardProps {
    delayHistoryData: DelayHistory[];
    selectedOption: 'origin' | 'destination';

}

// Function to map category names to colors
const getBarColor = (label: string, t: any) => {
    const early = t('statistics.delayCategories.early');
    const onTime = t('statistics.delayCategories.onTime');
    const fiveToNine = t('statistics.delayCategories.fiveToNine');
    const tenToFourteen = t('statistics.delayCategories.tenToFourteen');
    const fifteenToTwentyNine = t('statistics.delayCategories.fifteenToTwentyNine');
    const thirtyPlus = t('statistics.delayCategories.thirtyPlus');
    const cancelled = t('statistics.delayCategories.cancelled');
    
    switch (label) {
        case early: return slateColors[800];
        case onTime: return colors.green;
        case fiveToNine: return colors.orange;
        case tenToFourteen: return colors.orange;
        case fifteenToTwentyNine: return colors.red;
        case thirtyPlus: return colors.red;
        case cancelled: return colors.red;
        default: return slateColors[200];
    }
};

const DelayDistributionCard: React.FC<DelayDistributionCardProps> = ({ delayHistoryData, selectedOption }) => {
    const { t } = useTranslation();

    // Define 7 buckets: Early, On time (0-4 min), 5-9 min, 10-14 min, 15-29 min, 30+ min, Cancelled
    const buckets = [0, 0, 0, 0, 0, 0, 0]; // Adding an additional bucket for 'Cancelled'

    delayHistoryData.forEach(entry => {
        const delay = selectedOption === 'origin' ? entry.departureDelay : entry.arrivalDelay;
        const cancelled = selectedOption === 'origin'
        ? (entry.departureCancelled ?? entry.cancelled)
        : (entry.arrivalCancelled ?? entry.cancelled);

        if (cancelled === true) {
            buckets[6] += 1;  // Count cancellations in the last bucket
        } else if (delay != null) {
            const delayInMinutes = Math.floor(delay / 60);

            if (delayInMinutes < 0) buckets[0] += 1;              // Early
            else if (delayInMinutes <= 4) buckets[1] += 1;        // On time (0-4 min)
            else if (delayInMinutes <= 9) buckets[2] += 1;        // 5-9 min
            else if (delayInMinutes <= 14) buckets[3] += 1;       // 10-14 min
            else if (delayInMinutes <= 29) buckets[4] += 1;       // 15-29 min (combined)
            else buckets[5] += 1;                                 // 30+ min
        }
    });

    const totalEntries = buckets.reduce((sum, count) => sum + count, 0);
    const percentages = buckets.map(count => (count / totalEntries) * 100);

    // Define new X-axis labels using translations
    const xAxisLabels = [
        t('statistics.delayCategories.early'),
        t('statistics.delayCategories.onTime'),
        t('statistics.delayCategories.fiveToNine'),
        t('statistics.delayCategories.tenToFourteen'),
        t('statistics.delayCategories.fifteenToTwentyNine'),
        t('statistics.delayCategories.thirtyPlus'),
        t('statistics.delayCategories.cancelled')
    ];

    // Only display the "Early" bucket if there are values in it
    const filteredPercentages = percentages[0] > 0 ? percentages : percentages.slice(1);
    const filteredLabels = percentages[0] > 0 ? xAxisLabels : xAxisLabels.slice(1);

    return (
        <ContainerCard>
            <CardHeader>
                <TypographyStyles.BodyLSemibold color={slateColors[900]}>
                    {t('statistics.delayDistribution')}
                </TypographyStyles.BodyLSemibold>
                <TypographyStyles.BodyLRegular color={slateColors[600]}>
                    {t('statistics.last30Days')}
                </TypographyStyles.BodyLRegular>
            </CardHeader>
            <Chart>
                {filteredPercentages.map((percentage, index) => (
                    <Cohort key={index}>
                        <CategoryText>{filteredLabels[index]}</CategoryText>
                        <HorizontalBar>
                            <BarFill 
                                width={`${percentage}%`} 
                                color={getBarColor(filteredLabels[index], t)}
                            />
                        </HorizontalBar>
                        <ValueText>{percentage.toFixed(0)}%</ValueText>
                    </Cohort>
                ))}
            </Chart>
        </ContainerCard>
    );
};

export default DelayDistributionCard
